import { useEffect, useState } from "react";
import StackKit from "@/shared/components/kit/stack";
import TypoGraphyKit from "@/shared/components/kit/typoGraphy";
import CardBoxLayout from "../../layout/cardBoxLayout";
import { useForm } from "react-hook-form";
import ProfileFormSection from "./ProfileFormSection";
import CustomButton from "@/shared/components/kit/button";
import BorderColorIcon from '@mui/icons-material/BorderColor';
import { Divider, Grid, useMediaQuery, useTheme } from "@mui/material";
import { fetchPazirandeProfileInfo } from "@/api/merchant/request.get";
import { CircularProgress } from "@mui/material";
import { toPersianNumber } from "@/hooks/useThousandsSeparator";

export type MerchantProfile = {
  firstName: string;
  lastName: string;
  phone: string;
  birthDate: string;
  fatherName: string;
  businessName: string;
  jobCategory: string;
  merchantType: string;
  registerDate: string;
  branchCount: string;
  province: string;
  city: string;
  address: string;
  postalCode: string;
  hostProvince: string;
  hostCity: string;
  hostAddress: string;
  RecipientPostalCode: string;
  ReceiverContactNumber: string;
};

export type Device = {
  title: string;
  deviceID: string;
  deviceTerminalCode: string;
  acceptorCode: string;
}

export interface ReceivingDeviceInformation {
  devices: Device[];
}

const Devices: ReceivingDeviceInformation = {
  devices: [
    { title: "ATM Branch 1", deviceID: "DEV001", deviceTerminalCode: "TERM001", acceptorCode: "ACC001" },
    { title: "ATM Branch 2", deviceID: "DEV002", deviceTerminalCode: "TERM002", acceptorCode: "ACC002" },
    { title: "ATM Branch 3", deviceID: "DEV003", deviceTerminalCode: "TERM003", acceptorCode: "ACC003" },
  ],
};

export type AcceptingBranchInformation = {
  title: string;
  hostProvince: string;
  hostCity: string;
  hostAddress: string;
  codePostProvince: string;
  recipientPostalCode: string;
  receiverContactNumber: string;
}

export interface AcceptingBranchs {
  branch: AcceptingBranchInformation[];
}

const Branchs: AcceptingBranchs = {
  branch: [
    {
      title: "ATM Branch 1",
      hostProvince: "DEV001",
      hostCity: "تهران",
      hostAddress: "TERM001",
      codePostProvince: "ACC001",
      recipientPostalCode: "ACC001",
      receiverContactNumber: "ACC001",
    },
  ],
};
/**
 * ProfileMerchant
 *
 * این کامپوننت صفحه مدیریت و ویرایش اطلاعات پذیرنده را شامل سه بخش اصلی ارائه می‌دهد:
 *
 * 1. **اطلاعات پذیرنده (MerchantProfile)**  
 *    - نمایش اطلاعات هویتی و تجاری پذیرنده شامل نام، شماره تماس، دسته شغلی، تاریخ ثبت‌نام، آدرس و موارد مرتبط.  
 *    - قابلیت ویرایش اطلاعات از طریق React Hook Form.  
 *    - امکان ثبت و انصراف از تغییرات.
 *
 * 2. **اطلاعات دستگاه‌های پذیرنده (ReceivingDeviceInformation)**  
 *    - نمایش لیست دستگاه‌ها شامل عنوان، کد دستگاه، کد ترمینال و کد پذیرنده.  
 *    - قابلیت ویرایش داده‌ها به صورت داینامیک برای هر دستگاه.
 *
 * 3. **اطلاعات شعب پذیرنده (AcceptingBranchs)**  
 *    - نمایش جزئیات هر شعبه شامل استان، شهر، آدرس، کد پستی و شماره تماس.  
 *    - امکان ویرایش اطلاعات هر شعبه با ساختار مشابه سایر بخش‌ها.
 *
 * ویژگی‌های اصلی:
 * - استفاده از React Hook Form برای مدیریت داده‌ها و کنترل فیلدها.  
 * - استفاده از سه state مستقل برای تعیین وضعیت ویرایش هر بخش.  
 * - واکنش‌گرا بودن (Responsive) با استفاده از useMediaQuery برای ایجاد Divider عمودی یا افقی.  
 * - ساختاردهی و چیدمان با استفاده از Grid و StackKit برای نمایش صحیح در موبایل و دسکتاپ.
 *
 * @returns JSX.Element
 */

export default function ProfileMerchant() {
  const [isEditingMerchant, setIsEditingMerchant] = useState(false);
  const [isEditingDevices, setIsEditingDevices] = useState(false);
  const [isEditingBranches, setIsEditingBranches] = useState(false);
  const [apiDevices, setApiDevices] = useState<ReceivingDeviceInformation>({ devices: [] });
  const [loading, setLoading] = useState(true);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));

  const { control: merchantControl, handleSubmit: handleMerchantSubmit, reset: resetMerchant, watch: watchMerchant } = useForm<MerchantProfile>();
  const merchantData = watchMerchant();
  const { control: devicesControl, handleSubmit: handleDevicesSubmit, reset: resetDevices } = useForm({ defaultValues: Devices });
  // const { control: branchesControl, handleSubmit: handleBranchesSubmit, reset: resetBranches } = useForm({ defaultValues: Branchs });
  const mapProfileApiToMerchantProfile = (data: any): MerchantProfile => {
    return {
      firstName: data?.name ?? "-",
      lastName: data?.last_name ?? "-",
      phone: toPersianNumber(data?.phone_number) ?? "-",
      birthDate: toPersianNumber(data?.birthday) ?? "-",
      fatherName: data?.father_name ?? "-",
      businessName: toPersianNumber(data?.merchant_name) ?? "-",
      jobCategory: data?.category?.title ?? "-",
      merchantType: data?.type ?? "-",
      registerDate: toPersianNumber(data?.created_at?.split("T")[0]) ?? "-",
      branchCount: toPersianNumber(data?.imeis_count) ?? "-",
      province: data?.town ?? "-",
      city: data?.city ?? "-",
      address: data?.address ?? "-",
      postalCode: toPersianNumber(data?.post_code) ?? "-",
      hostProvince: data?.town ?? "-",
      hostCity: data?.city ?? "-",
      hostAddress: data?.address ?? "-",
      RecipientPostalCode: toPersianNumber(data?.post_code) ?? "-",
      ReceiverContactNumber: toPersianNumber(data?.phone_work) ?? "-",
    };
  };
  const onSubmitMerchant = (data: MerchantProfile) => {
    setIsEditingMerchant(false);
  };

  const onSubmitDevices = (data: typeof Devices) => {
    setIsEditingDevices(false);
  };

  const onSubmitBranches = (data: typeof Branchs) => {
    setIsEditingBranches(false);
  };

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const res = await fetchPazirandeProfileInfo();

        // Map merchant profile
        const mappedData = mapProfileApiToMerchantProfile(res.data);
        resetMerchant(mappedData);

        // Map API imei data to Device type
        const devicesFromApi: Device[] = res.data.imeis?.map((item: any) => ({
          title: item.merchant_name ?? "-",
          deviceID: item.imei ?? "-",
          deviceTerminalCode: toPersianNumber(item.terminal_number) ?? "-",
          acceptorCode: toPersianNumber(item.pazirande_number) ?? "-",
        })) || [];

        setApiDevices({ devices: devicesFromApi });
        resetDevices({ devices: devicesFromApi }); // populate form with API devices
      } catch (error) {
        console.error("Failed to load profile:", error);
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [resetMerchant, resetDevices]);

  if (loading) {
    return (
      <StackKit
        alignItems="center"
        justifyContent="center"
        sx={{ width: "100%", minHeight: "70vh" }}
      >
        <CircularProgress />
      </StackKit>
    );
  }

  return (
    <StackKit direction={'column'} gap={2} sx={{ width: "100%", position: "relative", minHeight: "476px" }}>
      {/* Merchant Info */}
      <CardBoxLayout sx={{ mt: 6, padding: 2, background: "linear-gradient(97.77deg, rgba(0,0,0,0.4) -9.73%, rgba(23,23,23,0.28) 16.04%, rgba(199,200,202,0.25) 50.23%, rgba(65,65,65,0.23) 87.66%, rgba(0,0,0,0.16) 109.42%)" }}>
        <StackKit direction="row" justifyContent="space-between" sx={{ pb: 1, mb: 2 }}>
          <TypoGraphyKit variant="h6">اطلاعات پذیرنده</TypoGraphyKit>
          <BorderColorIcon onClick={() => setIsEditingMerchant(p => !p)} className="w-[30px] h-[30px]" />
        </StackKit>

        <form onSubmit={handleMerchantSubmit(onSubmitMerchant)}>
          <StackKit direction={{ xs: "column", md: "column", lg: "row" }} sx={{ gap: { xs: 3, md: 0 } }}>
            <ProfileFormSection control={merchantControl} isEditing={isEditingMerchant} faceData={merchantData} fields={[
              { label: "نام مدیر", name: "firstName" },
              { label: "نام خانوادگی مدیر", name: "lastName" },
              { label: "شماره تماس", name: "phone" },
              { label: "تاریخ تولد", name: "birthDate" },
              { label: "نام پدر", name: "fatherName" },
            ]} />
            <Divider flexItem orientation={isMobile ? "horizontal" : "vertical"} sx={{ borderColor: "rgba(255,255,255,0.5)" }} />

            <ProfileFormSection control={merchantControl} isEditing={isEditingMerchant} faceData={merchantData} fields={[
              { label: "نام تجاری", name: "businessName" },
              { label: "دسته شغلی", name: "jobCategory" },
              { label: "نوع پذیرنده", name: "merchantType" },
              { label: "تاریخ ثبت‌نام", name: "registerDate" },
              { label: "تعداد شعبه‌ها", name: "branchCount" },
            ]} />
            <Divider flexItem orientation={isMobile ? "horizontal" : "vertical"} sx={{ borderColor: "rgba(255,255,255,0.5)" }} />

            <ProfileFormSection control={merchantControl} isEditing={isEditingMerchant} faceData={merchantData} fields={[
              { label: "استان پذیرنده", name: "hostProvince" },
              { label: "شهر پذیرنده", name: "hostCity" },
              { label: "آدرس پذیرنده", name: "hostAddress" },
              { label: "کد پستی پذیرنده", name: "RecipientPostalCode" },
              { label: "شماره تماس پذیرنده", name: "ReceiverContactNumber" },
            ]} />
          </StackKit>

          {isEditingMerchant && (
            <StackKit direction="row" justifyContent="flex-end" gap={2} sx={{ mt: 3 }}>
              <CustomButton sx={{ color: '#fff' }} type="submit">ثبت تغییرات</CustomButton>
              <CustomButton sx={{ color: '#fff' }} type="button" onClick={() => { resetMerchant(); setIsEditingMerchant(false); }}>انصراف</CustomButton>
            </StackKit>
          )}
        </form>
      </CardBoxLayout>

      {/* Devices & Branches */}
      <Grid container spacing={2} sx={{ mt: 2 }}>
        {/* Devices Section */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <CardBoxLayout
            sx={{
              padding: 2,
              background:
                "linear-gradient(97.77deg, rgba(0,0,0,0.4) -9.73%, rgba(23,23,23,0.28) 16.04%, rgba(199,200,202,0.25) 50.23%, rgba(65,65,65,0.23) 87.66%, rgba(0,0,0,0.16) 109.42%)",
            }}
          >
            <StackKit direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
              <TypoGraphyKit textAlign="right" variant="h6">
                اطلاعات دستگاه های پذیرنده
              </TypoGraphyKit>
              <BorderColorIcon
                onClick={() => setIsEditingDevices((p) => !p)}
                className="w-[30px] h-[30px]"
              />
            </StackKit>

            {apiDevices.devices.length > 0 ? (
              <form onSubmit={handleDevicesSubmit(onSubmitDevices)}>
                {apiDevices.devices.map((device, idx) => (
                  <ProfileFormSection
                    key={idx}
                    control={devicesControl as any}
                    isEditing={isEditingDevices}
                    faceData={device}
                    fields={[
                      { label: "عنوان دستگاه", name: "title" },
                      { label: "کد دستگاه", name: "deviceID" },
                      { label: "کد ترمینال", name: "deviceTerminalCode" },
                      { label: "کد پذیرنده", name: "acceptorCode" },
                    ]}
                  />
                ))}

                {isEditingDevices && (
                  <StackKit direction="row" justifyContent="flex-end" gap={2} sx={{ mt: 2 }}>
                    <CustomButton sx={{ color: "#fff" }} type="submit">
                      ثبت تغییرات
                    </CustomButton>
                    <CustomButton
                      sx={{ color: "#fff" }}
                      type="button"
                      onClick={() => {
                        resetDevices();
                        setIsEditingDevices(false);
                      }}
                    >
                      انصراف
                    </CustomButton>
                  </StackKit>
                )}
              </form>
            ) : (
              <TypoGraphyKit textAlign="right" variant="body1">
                اطلاعات دستگاهی موجود نیست
              </TypoGraphyKit>
            )}
          </CardBoxLayout>
        </Grid>

        {/* Branches Section */}
        {/* <Grid size={{xs:12 , sm:6}}>
          <CardBoxLayout sx={{ padding: 2, background: "linear-gradient(97.77deg, rgba(0,0,0,0.4) -9.73%, rgba(23,23,23,0.28) 16.04%, rgba(199,200,202,0.25) 50.23%, rgba(65,65,65,0.23) 87.66%, rgba(0,0,0,0.16) 109.42%)" }}>
            <StackKit direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
              <TypoGraphyKit textAlign="right" variant="h6">اطلاعات شعبه های پذیرنده</TypoGraphyKit>
              <BorderColorIcon onClick={() => setIsEditingBranches(p => !p)} className="w-[30px] h-[30px]" />
            </StackKit>

            <form onSubmit={handleBranchesSubmit(onSubmitBranches)}>
              {Branchs.branch.map((branch, idx) => (
                <ProfileFormSection<AcceptingBranchInformation>
                  key={idx}
                  control={branchesControl as any}
                  isEditing={isEditingBranches}
                  faceData={branch}
                  fields={[
                    { label: "عنوان شعبه", name: "title" },
                    { label: "استان", name: "hostProvince" },
                    { label: "شهر", name: "hostCity" },
                    { label: "آدرس", name: "hostAddress" },
                    { label: "کد پستی", name: "recipientPostalCode" },
                    { label: "شماره تماس", name: "receiverContactNumber" },
                  ]}
                />
              ))}

              {isEditingBranches && (
                <StackKit direction="row" justifyContent="flex-end" gap={2} sx={{ mt: 2 }}>
                  <CustomButton sx={{color:'#fff'}} type="submit">ثبت تغییرات</CustomButton>
                  <CustomButton sx={{color:'#fff'}} type="button" onClick={() => { resetBranches(); setIsEditingBranches(false); }}>انصراف</CustomButton>
                </StackKit>
              )}
            </form>
          </CardBoxLayout>
        </Grid> */}
      </Grid>
    </StackKit>
  );
}
